/**
 * MarketplacePage
 *
 * WP-SL-V3: passes userRoles to useCatalog for visibility filtering,
 * and feeds loaded tiles (not static config tiles) to CategorySidebar
 * so categories reflect the actual API-sourced catalogue.
 *
 * catalogue-card-v2: content column gap tightened to `--sp-4` to match
 * the card grid's own gap token; no logic change.
 *
 * brief-13-shell-nav-rail: CategorySidebar is now a horizontal tab row
 * above the grid instead of a left-hand column, so the page is a single
 * full-width column rather than a two-column flex split.
 */

import { useCallback, useMemo, useState } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useAuth } from '../auth/KeycloakProvider';
import { useCatalog, type CatalogState } from '../hooks/useCatalog';
import { t } from '../i18n';
import CategorySidebar from '../components/CategorySidebar';
import ViewToggle from '../components/ViewToggle';
import { sortPinnedFirst } from '../hooks/useCatalogPrefs';
import { usePrefs } from '../prefs/PrefsContext';
import TileGrid, { TileGridSkeleton } from '../components/TileGrid';
import EmptyState from '../components/EmptyState';
import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import { isAuthenticatedBroker, VERTICAL_BROKER_MODE } from '../config/schema';
import { planeForMode } from '../shell/routes';
import { canManageApp, canOrderForTenant, type CallerIdentity } from '../auth/surfaceAccess';
import type { CartItem } from './CartPage';

interface MarketplacePageProps {
  previewState?: CatalogState['tag'];
  onAddToCart?: (item: CartItem) => void;
  onNavigate?: (path: Route) => void;
}

export default function MarketplacePage({ previewState, onAddToCart, onNavigate }: MarketplacePageProps) {
  const config = useConfig();
  const { token, getToken, user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');

  // Any authenticated broker (meta, tenant, or vertical-broker) reads its catalogue
  // from the API and binds through POST /bindings.
  const isApiCatalog = isAuthenticatedBroker(config.instance.mode);
  // Stable identity: a fresh [] every render re-triggers useCatalog's effect
  // (userRoles is in its deps) and permanently restarts the skeleton timer
  // when auth is disabled and `user` is null.
  const userRoles = useMemo(() => user?.roles ?? [], [user]);
  // decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench.
  //
  // Two questions, two answers, never one boolean again:
  //
  //   plane      — WHAT KIND OF DEPLOYMENT IS THIS. A property of the instance,
  //                answered once in shell/routes planeForMode(). It owns card
  //                SHAPE: lifecycle badge vs tenant broker badge, commerce CTA,
  //                pricing overlay, muting, pinning, the list view.
  //   canManage  — WHAT MAY THIS CALLER DO WITH THIS APP. A property of the
  //                token × the tile, answered once in auth/surfaceAccess
  //                canManageApp(). It owns the Manage affordance and nothing else.
  //
  // The 2026-08-01 violation (tenant-admin-e2e reaching Deploy / Edit description
  // on dev.deal.do.place) came from conflating them. It cannot recur through this
  // page: there are no mutation affordances left on a tile to leak. Manage is a
  // POINTER to /apps/:id/operations, and every control on that board carries its
  // own role predicate with the server gate behind it.
  const plane = planeForMode(config.instance.mode);
  const management = plane === 'management';
  const caller = useMemo<CallerIdentity>(
    () => ({
      company: user?.company ?? null,
      hasRole: (role: string) => userRoles.includes(role),
    }),
    [user, userRoles],
  );
  // Per TILE, not per page: on the shared vertical broker one tenant-admin sees
  // Manage on their own tenant's apps and on no others.
  const canManage = useCallback(
    (tile: TileConfig) => canManageApp(caller, tile.tenant),
    [caller],
  );

  /**
   * decision-2026-08-02-ordering-is-a-tenant-admin-verb: ordering an app commits the
   * TENANT to an entitlement and a cost. It is procurement, not consumption, so by the
   * test in decision-2026-08-02-tenant-broker-is-user-oriented it is not a task a USER
   * performs and does not belong on the tenant broker at all — it is a tenant-admin verb
   * executed on the vertical broker.
   *
   * This is deliberately a THIRD predicate, kept out of `plane` and `canManage` — and out
   * of the `isAdminCatalog` boolean those two replaced. That boolean answered two questions
   * at once ("which broker is this?" AND "may this identity mutate the registry?"), and
   * because TENANT_MODE is absent from isAdminSurfaceMode it was always false on a tenant
   * store — which is exactly how a plain tenant user came to see a red Order pill on
   * dev.mtcm.do.place. The fix is NOT to add TENANT_MODE to isAdminSurfaceMode, and NOT to
   * fold commerce into `plane`: `plane` owns card SHAPE, so either move would drag the
   * lifecycle badge, the detail overlay and the muting along with it. Separate question,
   * separate flag.
   *
   * Stated positively so the safe direction is the default: only the UNAUTHENTICATED
   * marketing modes carry a commerce affordance. Any future authenticated mode gets none
   * until someone decides otherwise. convention-admin-catalogue-no-commerce-cta Am4.
   */
  const showCommerce =
    config.instance.mode === 'tier-0' || config.instance.mode === 'reference';

  /**
   * wp-order-surface-vertical-broker: the Order affordance the vertical broker
   * carries and no other authenticated surface does. Ordering is a tenant-admin verb
   * executed HERE (decision-2026-08-02-ordering-is-a-tenant-admin-verb), so this is
   * gated by mode (only the vertical broker) AND, per tile, by canOrderForTenant —
   * a tenant-admin for their own tenant, a broker-admin on behalf. It is a FOURTH
   * predicate, kept apart from showCommerce (storefront shape), `plane` (card shape)
   * and canManage (the Manage affordance): folding it into any of them would drag an
   * unrelated behaviour along. The tenant store, on the consumption plane, is never
   * the vertical broker and so stays CTA-free.
   */
  const canOrder = useCallback(
    (tile: TileConfig) =>
      config.instance.mode === VERTICAL_BROKER_MODE && canOrderForTenant(caller, tile.tenant),
    [caller, config.instance.mode],
  );

  // brief-26: pins and view mode are per browser, per instance, per user
  // (see useCatalogPrefs for why that scope and what it costs).
  const { prefs, togglePin, setView } = usePrefs();

  const { state, retry } = useCatalog({
    tiles: config.catalog.tiles,
    activeCategory,
    search,
    mode: config.instance.mode,
    token,
    getToken,
    userRoles,
    preview: previewState,
  });

  // In vertical-ref/vertical-broker mode, use loaded tiles for sidebar so tags are
  // API-accurate. Fall back to config tiles (empty there, used by other modes).
  const sidebarTiles: TileConfig[] = isApiCatalog && state.tag === 'loaded'
    ? state.tiles
    : config.catalog.tiles;

  function clearFilters() {
    setActiveCategory('All');
    setSearch('');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', width: '100%' }}>
      <CategorySidebar
        tiles={sidebarTiles}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', maxWidth: '1200px' }}>
        {/* Search + eyebrow */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-4)' }}>
          <p className="dp-eyebrow">{t('marketplace.eyebrow')}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
            {management && <ViewToggle view={prefs.view} onChange={setView} />}
            <input
            type="search"
            placeholder={t('marketplace.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: '6px var(--sp-3)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'var(--border-default)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              background: 'var(--bg-surface)',
              color: 'var(--fg-1)',
              width: '220px',
            }}
            />
          </div>
        </div>

        {/* Data state machine */}
        {state.tag === 'loading' && <TileGridSkeleton count={config.catalog.tiles.length || 6} />}

        {state.tag === 'loaded' && (
          <TileGrid
            tiles={management ? sortPinnedFirst(state.tiles, prefs.pinned) : state.tiles}
            useBindingFlow={isApiCatalog}
            onAddToCart={onAddToCart}
            plane={plane}
            canManage={canManage}
            canOrder={canOrder}
            showCommerce={showCommerce}
            onNavigate={onNavigate}
            view={prefs.view}
            pinnedIds={prefs.pinned}
            onTogglePin={management ? togglePin : undefined}
          />
        )}

        {state.tag === 'empty-no-results' && (
          <EmptyState variant="no-results" onAction={clearFilters} />
        )}

        {state.tag === 'empty-no-access' && (
          <EmptyState variant="no-access" />
        )}

        {state.tag === 'empty-fetch-failed' && (
          <EmptyState
            variant="fetch-failed"
            errorCode={state.errorCode}
            onAction={retry}
          />
        )}
      </div>
    </div>
  );
}
